<template>
    <v-container fluid>
        <v-data-table
            :headers="headers"
            :items="productsFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">PRODUCTS</h1>
                        <v-btn
                            v-if="editSettingsPermission && !loading"
                            rounded
                            color="primary"
                            @click="openCreateProduct"
                            class="mx-2"
                        >
                            <v-icon>
                                mdi-plus
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'NEW' }}
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.name`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByName"
                    dense
                    prepend-icon="mdi-filter"
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn
                        small
                        icon
                        @click="openEditProduct(item)"
                        :disabled="!editSettingsPermission || loading"
                    >
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                        small
                        icon
                        @click="openDeleteProduct(item)"
                        :disabled="!editSettingsPermission || loading"
                    >
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <!--PRODUCT FORM-->
        <v-dialog
            v-model="openProductForm"
            :retain-focus="false"
            persistent
            width="400px"
        >
            <ProductsForm
                v-if="openProductForm"
                :createForm="createForm"
                :product="selectedProduct"
                :originalProduct="originalProduct"
                :productsSettings="productsSettings"
                @addProduct="addProduct"
                @replaceProduct="replaceProduct"
                @close="closeEditProduct"
            />
        </v-dialog>
        <!--Delete Product-->
        <v-dialog
            v-model="openDeleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="openDeleteWarning">
                <v-card-title class="text-h5">Delete Product</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this product?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteProduct">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteProduct"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import _ from 'lodash'

export default {
    name: 'Products',
    props: {
        setting: {
            type: Object,
            default: () => ({}),
        },
    },
    components: {
        ProductsForm: () => import('@/components/Products/ProductsForm.vue'),
    },
    data: () => ({
        loading: false,
        products: [],
        selectedProduct: {},
        originalProduct: {},
        createForm: true,
        openProductForm: false,
        openDeleteWarning: false,
        productsSettings: {},
        headers: [
            {
                text: 'NAME',
                value: 'name',
                sortable: false,
                width: '300',
            },
            {
                text: 'ABBREVIATION',
                value: 'abbreviation',
                sortable: false,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                sortable: false,
                align: 'center',
            },
        ],
        searchByName: undefined,
        editSettingsPermission: false,
    }),
    async mounted() {
        try {
            this.productsSettings =
                this.setting.name == 'WorkOrders'
                    ? _.cloneDeep(this.setting.products)
                    : this.setting

            this.loading = true
            const products = await API.getProducts()
            this.products = products.data.products
            // get auth user
            const {
                data: { user },
            } = await API.getMyInfo()
            // Set edit settings permission
            this.editSettingsPermission = user.permissions.includes(
                'editSettings'
            )
        } catch (error) {
            this.setErrorItems({
                name: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        productsFiltered() {
            const conditions = []
            if (this.searchByName) {
                conditions.push(this.filterByName)
            }
            if (conditions.length > 0) {
                return this.products.filter(product => {
                    return conditions.every(condition => {
                        return condition(product)
                    })
                })
            }
            return this.products
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        filterByName(item) {
            return item.name
                .toLowerCase()
                .includes(this.searchByName.toLowerCase())
        },
        replaceProduct(productToUpdate) {
            const index = this.products.findIndex(
                product => product.id == productToUpdate.id
            )
            if (index >= 0) {
                this.products.splice(index, 1, {
                    ...this.products[index],
                    ...productToUpdate,
                })
            }
        },
        addProduct(product) {
            this.products.push(product)
        },
        removeProduct() {
            const index = this.products.findIndex(
                product => product.id == this.selectedProduct.id
            )
            if (index >= 0) {
                this.products.splice(index, 1)
            }
        },
        async deleteProduct() {
            try {
                this.loading = true
                await API.deleteProduct({
                    settingId: this.productsSettings.id,
                    productId: this.selectedProduct.id,
                })
                this.removeProduct()
                this.closeDeleteProduct()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeDeleteProduct() {
            this.selectedProduct = {}
            this.openDeleteWarning = false
        },
        openDeleteProduct(item) {
            this.selectedProduct = _.cloneDeep(item)
            this.openDeleteWarning = true
        },
        closeEditProduct() {
            this.createForm = true
            this.selectedProduct = {}
            this.originalProduct = {}
            this.openProductForm = false
        },
        openEditProduct(item) {
            this.createForm = false
            this.selectedProduct = _.cloneDeep(item)
            this.originalProduct = _.cloneDeep(item)
            this.openProductForm = true
        },
        openCreateProduct() {
            this.createForm = true
            this.openProductForm = true
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
}
.v-input .v-label {
    font-size: 12px;
}
</style>
